import { useEffect } from 'react'
import { MOBILE_QUERY } from './mobile'

let users = 0
let dispose: (() => void) | undefined

/** Resize the app to the visible area above the keyboard; pinch zoom keeps its layout. */
export function bindChatViewport(): () => void {
  if (users++ === 0) {
    const root = document.documentElement
    const viewport = window.visualViewport
    const mq = window.matchMedia(MOBILE_QUERY)
    let fullHeight = window.innerHeight
    let width = window.innerWidth
    let frame: number | undefined
    let settle: ReturnType<typeof setTimeout> | undefined
    const clear = () => {
      root.style.removeProperty('--chat-viewport-height')
      root.style.removeProperty('--chat-viewport-top')
      delete root.dataset.keyboard
    }
    const update = () => {
      if (!mq.matches || !viewport) { clear(); return }
      // Never reflow the app as the user zooms in on an image or text.
      if (Math.abs(viewport.scale - 1) > 0.05) return
      // Some standalone browsers shrink innerHeight along with the keyboard.
      // Retain the unfocused height so their bottom safe area is not counted twice.
      if (window.innerWidth !== width) { width = window.innerWidth; fullHeight = window.innerHeight }
      fullHeight = Math.max(fullHeight, window.innerHeight, viewport.height)
      root.style.setProperty('--chat-viewport-height', `${viewport.height}px`)
      root.style.setProperty('--chat-viewport-top', `${viewport.offsetTop}px`)
      root.dataset.keyboard = fullHeight - viewport.height > 120 ? 'true' : 'false'
    }
    const schedule = () => {
      update()
      if (frame !== undefined) cancelAnimationFrame(frame)
      clearTimeout(settle)
      frame = requestAnimationFrame(update)
      // Safari's viewport metrics can settle after the keyboard animation.
      settle = setTimeout(update, 350)
    }
    viewport?.addEventListener('resize', update)
    viewport?.addEventListener('scroll', update)
    window.addEventListener('resize', update)
    window.addEventListener('pageshow', schedule)
    document.addEventListener('focusin', schedule)
    document.addEventListener('focusout', schedule)
    document.addEventListener('visibilitychange', schedule)
    mq.addEventListener('change', update)
    update()
    dispose = () => {
      viewport?.removeEventListener('resize', update)
      viewport?.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
      window.removeEventListener('pageshow', schedule)
      document.removeEventListener('focusin', schedule)
      document.removeEventListener('focusout', schedule)
      document.removeEventListener('visibilitychange', schedule)
      mq.removeEventListener('change', update)
      if (frame !== undefined) cancelAnimationFrame(frame)
      clearTimeout(settle)
      clear()
    }
  }
  return () => { if (--users === 0) { dispose?.(); dispose = undefined } }
}

export function useChatViewport(): void { useEffect(bindChatViewport, []) }
